<template>
    <div class="share-contracts-modal flex flex-col gap-10">
        <p class="share-contracts-modal__description">
            {{ $t('§contracts.share_modal_description', { count: contracts.length }) }}
        </p>

        <CoreDropdown
            v-model="selectedPartner"
            :options="optionsStore.partnersOptions"
            :filter="optionsStore.partnersOptions.length > 8"
            placeholder="§ui.placeholder_select_partner"
        />

        <div v-if="previewLoading" class="share-contracts-modal__loading">
            {{ $t('§ui.loading') }}
        </div>

        <template v-else-if="selectedPartner && preview">
            <Message v-if="!hasTargetEntity" severity="error" :closable="false">
                {{ $t('§contracts.share_no_billing_entity') }}
            </Message>

            <template v-else>
                <div
                    v-if="mismatching.length"
                    class="share-contracts-modal__notice share-contracts-modal__notice--switch"
                >
                    <span class="share-contracts-modal__notice-heading">
                        {{
                            $t('§contracts.share_switch_notice', {
                                count: mismatching.length,
                                entity: targetEntityName,
                            })
                        }}
                    </span>
                    <div class="share-contracts-modal__switch-list">
                        <CoreCheckbox
                            v-for="contract in mismatching"
                            :key="contract.id"
                            v-model="switchSelections[contract.id]"
                            :name="`switch-${contract.id}`"
                            :label="contractLabel(contract)"
                        />
                    </div>
                </div>

                <Message
                    v-if="allAlreadyShared"
                    severity="warn"
                    :closable="false"
                >
                    {{ $t('§contracts.share_all_already_shared') }}
                </Message>
                <div
                    v-else-if="alreadySharedContracts.length"
                    class="share-contracts-modal__notice share-contracts-modal__notice--shared"
                >
                    <span class="share-contracts-modal__notice-heading">
                        {{
                            $t('§contracts.share_some_already_shared', {
                                count: alreadySharedContracts.length,
                            })
                        }}
                    </span>
                    <ul class="share-contracts-modal__shared-list">
                        <li v-for="contract in alreadySharedContracts" :key="contract.id">
                            {{ contractLabel(contract) }}
                        </li>
                    </ul>
                </div>
            </template>
        </template>

        <div class="flex justify-start gap-2">
            <CoreButton
                label="§ui_button.confirm"
                icon="pi-check"
                variant="primary"
                :disabled="!canConfirm"
                :loading="contractStore.sharing"
                @click="handleConfirm"
            />
            <CoreButton
                label="§ui_button.cancel"
                icon="pi-times"
                variant="tertiary"
                :disabled="contractStore.sharing"
                @click="handleCancel"
            />
        </div>
    </div>
</template>

<script>
import { useModalStore } from '@/stores/modal'
import { useOptionsStore } from '@/stores/options'
import { useContractStore } from '@/stores/contract'
import CoreDropdown from '@/components/core/CoreDropdown.vue'
import CoreButton from '@/components/core/CoreButton.vue'
import CoreCheckbox from '@/components/core/CoreCheckbox.vue'
import Message from 'primevue/message'

export default {
    name: 'ShareContractsModal',

    components: { CoreDropdown, CoreButton, CoreCheckbox, Message },

    data() {
        return {
            modalStore: useModalStore(),
            optionsStore: useOptionsStore(),
            contractStore: useContractStore(),
            selectedPartner: null,
            contracts: [],
            preview: null,
            previewLoading: false,
            switchSelections: {},
        }
    },

    mounted() {
        this.contracts = this.modalStore.data.contracts || []
    },

    watch: {
        selectedPartner() {
            this.loadPreview()
        },
    },

    computed: {
        hasTargetEntity() {
            return !!this.preview?.target_billing_entity
        },

        targetEntityName() {
            return this.preview?.target_billing_entity?.name || ''
        },

        mismatching() {
            return this.preview?.mismatching || []
        },

        matchingIds() {
            return this.preview?.matching_ids || []
        },

        alreadySharedContracts() {
            const ids = this.preview?.already_shared_ids || []
            return this.contracts.filter((contract) => ids.includes(contract.id))
        },

        allAlreadyShared() {
            return (
                this.hasTargetEntity &&
                this.contracts.length > 0 &&
                this.alreadySharedContracts.length === this.contracts.length
            )
        },

        checkedSwitchIds() {
            return this.mismatching
                .filter((contract) => this.switchSelections[contract.id])
                .map((contract) => contract.id)
        },

        shareCount() {
            return this.matchingIds.length + this.checkedSwitchIds.length
        },

        canConfirm() {
            return (
                !!this.selectedPartner &&
                this.hasTargetEntity &&
                this.shareCount > 0 &&
                !this.previewLoading &&
                !this.contractStore.sharing
            )
        },
    },

    methods: {
        async loadPreview() {
            this.preview = null
            this.switchSelections = {}
            if (!this.selectedPartner) return

            this.previewLoading = true
            try {
                const data = await this.contractStore.previewShare({
                    contractIds: this.contracts.map((contract) => contract.id),
                    partnerId: this.selectedPartner,
                })
                this.preview = data

                const selections = {}
                ;(data.mismatching || []).forEach((contract) => {
                    selections[contract.id] = false
                })
                this.switchSelections = selections
            } catch (error) {
                console.error(
                    'Error previewing share (views/Contract/ShareContractsModal):',
                    error
                )
                this.preview = null
            } finally {
                this.previewLoading = false
            }
        },

        contractLabel(contract) {
            return contract.name ? `#${contract.id} (${contract.name})` : `#${contract.id}`
        },

        handleCancel() {
            this.modalStore.setClose()
        },

        async handleConfirm() {
            const contractIds = [...this.matchingIds, ...this.checkedSwitchIds]
            if (!this.selectedPartner || contractIds.length === 0) return

            await this.contractStore.shareContracts({
                contractIds,
                partnerId: this.selectedPartner,
                switchContractIds: this.checkedSwitchIds,
            })

            this.modalStore.data.onShared?.()
            this.modalStore.setClose()
        },
    },
}
</script>

<style scoped>
.share-contracts-modal__description {
    font-size: 1.5rem;
    color: var(--text-dark);
}

.share-contracts-modal__loading {
    color: var(--text-dark);
}

.share-contracts-modal__notice {
    display: flex;
    flex-direction: column;
    gap: 6px;
    padding: var(--space-6);
    border-radius: 4px;
    font-size: var(--text-base);
    color: var(--text-dark);
}

.share-contracts-modal__notice--switch {
    background-color: var(--color-yellow-200);
}

.share-contracts-modal__notice--shared {
    background-color: var(--color-blue-200);
}

.share-contracts-modal__notice-heading {
    font-size: var(--text-base);
}

.share-contracts-modal__switch-list {
    display: flex;
    flex-direction: column;
    gap: 4px;
    margin-top: 4px;
}

.share-contracts-modal__shared-list {
    margin: 0;
    padding-left: 20px;
    list-style: disc;
    font-size: var(--text-base);
}

.share-contracts-modal__shared-list li {
    margin: 2px 0;
}
</style>
